import { Link } from "react-router-dom";
import { useState, FormEvent } from "react";
import { useShopifyProducts } from "@/context/ShopifyProductsContext";

const FEATURED_IDS = ["7820395151421", "7820420939837", "7820421267517"];

const ACCENT_STYLES = [
  {
    borderColor: "border-cyan-200 hover:border-cyan-400",
    badgeColor: "bg-cyan-500",
    textColor: "text-cyan-700",
    iconColor: "text-cyan-600",
    bgGradient: "from-cyan-50 via-white to-white",
  },
  {
    borderColor: "border-emerald-200 hover:border-emerald-400",
    badgeColor: "bg-emerald-600",
    textColor: "text-emerald-700",
    iconColor: "text-emerald-600",
    bgGradient: "from-emerald-50 via-white to-white",
  },
  {
    borderColor: "border-amber-200 hover:border-amber-400",
    badgeColor: "bg-amber-500",
    textColor: "text-amber-700",
    iconColor: "text-amber-600",
    bgGradient: "from-amber-50 via-white to-white",
  },
];

const ECOFLOW_PRODUCTS = ["DELTA 2", "DELTA Pro", "RIVER 2 Pro", "Smart Home Panel"];

export default function ShopByBrand() {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [ecoEmail, setEcoEmail] = useState("");
  const [ecoStatus, setEcoStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const { products, productNodes, loading } = useShopifyProducts();

  const handleEcoNotify = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!ecoEmail.trim()) return;
    setEcoStatus("submitting");
    try {
      const body = new URLSearchParams();
      body.append("email", ecoEmail.trim());
      body.append("brand", "EcoFlow");
      body.append("source", "Homepage — ShopByBrand EcoFlow Card");
      const res = await fetch("https://readdy.ai/api/form/d77qgkadqn9mib2tqc3g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setEcoStatus(res.ok ? "success" : "error");
      if (res.ok) setEcoEmail("");
    } catch {
      setEcoStatus("error");
    }
  };

  const featuredProducts = FEATURED_IDS.map((id) => {
    const product = products.find((p) => p.id === id);
    const node = productNodes[id];
    return { product, node };
  }).filter((item) => item.product && item.node);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold px-3 py-1 rounded-full">
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-award-fill"></i>
                </div>
                4 Authorized Reseller Partners
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight tracking-tight">
              Shop by Brand
            </h2>
            <p className="text-gray-500 text-sm mt-2 max-w-xl leading-relaxed">
              Every brand you see here has verified our reseller credentials — your warranty is valid, your purchase is protected, and you&apos;re buying the real thing, not gray-market hardware.
            </p>
          </div>
          <Link
            to="/shop"
            className="hidden md:flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-amber-600 transition-colors whitespace-nowrap cursor-pointer shrink-0"
          >
            View All Products
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </div>
          </Link>
        </div>

        {/* Product cards */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="rounded-2xl border-2 border-gray-100 bg-gray-50 animate-pulse h-80" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {featuredProducts.map(({ product, node }, idx) => {
              if (!product || !node) return null;
              const style = ACCENT_STYLES[idx % ACCENT_STYLES.length];
              const imageUrl = node.images.edges[0]?.node.url || product.image;
              const price = product.price;
              const originalPrice = product.originalPrice;
              const numericId = product.id;
              const brand = product.brand;
              const highlights = product.highlights.slice(0, 3);

              return (
                <Link
                  to={`/shop/${numericId}`}
                  key={numericId}
                  data-product-shop
                  className={`group rounded-2xl border-2 ${style.borderColor} bg-gradient-to-b ${style.bgGradient} overflow-hidden transition-all duration-300 cursor-pointer`}
                  onMouseEnter={() => setActiveIdx(idx)}
                  onMouseLeave={() => setActiveIdx(null)}
                >
                  {/* Product image */}
                  <div className="relative overflow-hidden h-44 bg-white">
                    <img
                      src={imageUrl}
                      alt={product.name}
                      title={`${product.name} — Authorized Reseller at Iron Haven Supply`}
                      className="w-full h-full object-contain object-center transition-transform duration-500 group-hover:scale-105 p-4"
                    />
                    <span className={`absolute top-3 left-3 ${style.badgeColor} text-white text-[10px] font-bold px-2.5 py-1 rounded-full`}>
                      {brand}
                    </span>
                    <span className="absolute top-3 right-3 flex items-center gap-1 bg-white/90 border border-gray-100 text-gray-600 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-shield-check-fill text-amber-500"></i>
                      </div>
                      Authorized
                    </span>
                  </div>

                  {/* Card body */}
                  <div className="px-4 py-4">
                    <h3 className="text-sm font-extrabold text-gray-900 tracking-tight leading-snug line-clamp-2 mb-2">{product.name}</h3>

                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-base font-bold text-gray-900">
                        ${price.toLocaleString()}
                      </span>
                      {originalPrice && (
                        <span className="text-xs text-gray-400 line-through">
                          ${originalPrice.toLocaleString()}
                        </span>
                      )}
                    </div>

                    {highlights.length > 0 && (
                      <ul className="space-y-1 mb-3">
                        {highlights.map((h) => (
                          <li key={h} className="flex items-center gap-1.5 text-xs text-gray-600">
                            <div className="w-3 h-3 flex items-center justify-center shrink-0">
                              <i className={`ri-check-line ${style.iconColor} text-xs`}></i>
                            </div>
                            <span className="line-clamp-1">{h}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <span className={`text-xs font-semibold ${style.iconColor} flex items-center gap-1`}>
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-shield-check-line"></i>
                        </div>
                        Warranty valid
                      </span>
                      <span className={`text-xs font-bold flex items-center gap-1 transition-all duration-200 ${activeIdx === idx ? "text-amber-500" : "text-gray-400"}`}>
                        View
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className={`ri-arrow-right-line transition-transform duration-200 ${activeIdx === idx ? "translate-x-0.5" : ""}`}></i>
                        </div>
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}

            {/* EcoFlow — Coming Soon card */}
            <div className="group rounded-2xl border-2 border-dashed border-gray-200 bg-gray-950 overflow-hidden relative flex flex-col">
              {/* Background image with overlay */}
              <div className="absolute inset-0">
                <img
                  src="https://public.readdy.ai/ai/img_res/5f36fb01165459e23db8a57ccd9a0d89.jpg"
                  alt="EcoFlow coming soon to Iron Haven Supply"
                  className="w-full h-full object-cover object-center opacity-20"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-gray-950/60 via-gray-950/70 to-gray-950" />
                <div
                  className="absolute inset-0 opacity-25 pointer-events-none"
                  style={{ backgroundImage: "radial-gradient(ellipse at 50% 30%, #f59e0b 0%, transparent 60%)" }}
                />
              </div>

              {/* Image area */}
              <div className="relative h-44 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-14 h-14 flex items-center justify-center bg-amber-500/20 border border-amber-500/30 rounded-2xl mx-auto mb-2">
                    <i className="ri-leaf-line text-amber-400 text-2xl"></i>
                  </div>
                  <span className="inline-flex items-center gap-1.5 bg-amber-500/20 border border-amber-500/30 text-amber-400 text-[10px] font-bold px-2.5 py-1 rounded-full">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-time-line text-[10px]"></i>
                    </div>
                    Coming Soon
                  </span>
                </div>
              </div>

              {/* Card body */}
              <div className="relative px-4 py-4 flex flex-col flex-1">
                <h3 className="text-sm font-extrabold text-white tracking-tight leading-snug mb-1">EcoFlow</h3>
                <p className="text-gray-500 text-xs leading-relaxed mb-3">
                  DELTA &amp; RIVER series — authorized reseller onboarding in progress.
                </p>

                {/* Mini product list */}
                <ul className="space-y-1 mb-4">
                  {ECOFLOW_PRODUCTS.map((p) => (
                    <li key={p} className="flex items-center gap-1.5 text-xs text-gray-500">
                      <div className="w-3 h-3 flex items-center justify-center shrink-0">
                        <i className="ri-time-line text-gray-600 text-[10px]"></i>
                      </div>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>

                {/* Notify form */}
                <div className="mt-auto pt-3 border-t border-white/10">
                  {ecoStatus === "success" ? (
                    <div className="flex items-center gap-2 bg-emerald-500/15 border border-emerald-500/25 rounded-lg px-3 py-2.5">
                      <div className="w-5 h-5 flex items-center justify-center shrink-0">
                        <i className="ri-check-line text-emerald-400 text-sm"></i>
                      </div>
                      <p className="text-emerald-300 text-xs font-semibold">You&apos;re on the list!</p>
                    </div>
                  ) : (
                    <form
                      data-readdy-form
                      id="ecoflow-brand-card-notify"
                      onSubmit={handleEcoNotify}
                      className="flex flex-col gap-2"
                    >
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="Your email — notify me"
                        value={ecoEmail}
                        onChange={(e) => setEcoEmail(e.target.value)}
                        className="w-full bg-white/10 border border-white/15 text-white placeholder-gray-600 text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-amber-400/60 transition-colors"
                      />
                      <button
                        type="submit"
                        disabled={ecoStatus === "submitting"}
                        className="w-full flex items-center justify-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white text-xs font-bold px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {ecoStatus === "submitting" ? (
                          <>
                            <div className="w-3.5 h-3.5 flex items-center justify-center">
                              <i className="ri-loader-4-line animate-spin text-xs"></i>
                            </div>
                            Sending...
                          </>
                        ) : (
                          <>
                            <div className="w-3.5 h-3.5 flex items-center justify-center">
                              <i className="ri-bell-line text-xs"></i>
                            </div>
                            Notify Me at Launch
                          </>
                        )}
                      </button>
                      {ecoStatus === "error" && (
                        <p className="text-red-400 text-[10px] text-center">Failed — please try again.</p>
                      )}
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bottom trust strip */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 bg-gray-50 rounded-xl px-6 py-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
              <i className="ri-verified-badge-line text-amber-600 text-lg"></i>
            </div>
            <p className="text-sm text-gray-600">
              <strong className="text-gray-900">Every brand listed here has verified Iron Haven Supply</strong> as an authorized dealer. Your purchase includes full manufacturer warranty coverage — no exceptions.
            </p>
          </div>
          <Link
            to="/about"
            className="text-xs font-semibold text-amber-600 hover:text-amber-500 whitespace-nowrap flex items-center gap-1 cursor-pointer transition-colors shrink-0"
          >
            Why it matters
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
